import {
  Box,
  Button,
  Callout,
  Code,
  Flex,
  Grid,
  Heading,
  Separator,
  Tabs,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import { AlertCircle, CalendarClock, User } from "lucide-react";
import { SubmitEvent, useState } from "react";
import {
  ManagerBlogFormData,
  ManagerBlogFormPaths,
  ManagerBlogsFormProps,
} from "@/types/manager_blogs";
import MarkdownPreview from "@/shared/manager/blog/MarkdownPreview";
import { blogSlug } from "@/shared/manager/blog/slug";
import useIsMobile from "@/shared/hooks/useIsMobile";

// The cheat sheet under the editor. Everything listed here survives the blog's
// sanitizer allow list (FinalBoutHelper::BLOG_TAGS) — an example that got
// stripped on the way to the page would be worse than no example at all.
//
// `##` rather than `#` for the heading: the post's own title is already the
// page's <h1>, so a body that opens with one produces a second.
const MARKDOWN_HINTS: { syntax: string; means: string }[] = [
  { syntax: "## Heading", means: "A heading — more #s, smaller heading" },
  { syntax: "**bold**", means: "Bold" },
  { syntax: "*italic*", means: "Italic" },
  { syntax: "[text](https://…)", means: "A link" },
  { syntax: "- item", means: "A bulleted list" },
  { syntax: "1. item", means: "A numbered list" },
  { syntax: "> quoted", means: "A pull quote" },
  { syntax: "---", means: "A horizontal rule" },
  { syntax: "![alt text](https://…)", means: "An image, by URL" },
];

interface BlogFormProps {
  data: ManagerBlogFormData;
  setData: <K extends keyof ManagerBlogFormData>(
    key: K,
    value: ManagerBlogFormData[K],
  ) => void;
  onSubmit: (e: SubmitEvent) => void;
  processing: boolean;
  errors?: string[];
  paths: ManagerBlogFormPaths;
  limits: ManagerBlogsFormProps["limits"];
  title: string;
  submitLabel: string;
}

export default function BlogForm({
  data,
  setData,
  onSubmit,
  processing,
  errors,
  paths,
  limits,
  title,
  submitLabel,
}: BlogFormProps) {
  const isMobile = useIsMobile();

  // A post that already has a slug keeps it: the URL is settled and rewording
  // the title must not move it. A new one follows the title until somebody
  // types in the slug field themselves.
  const [slugPinned, setSlugPinned] = useState(Boolean(data.blog.slug));

  const setBlog = (fields: Partial<ManagerBlogFormData["blog"]>) => {
    const next = { ...data.blog, ...fields };

    if (!slugPinned && ("title" in fields || "publish_at" in fields)) {
      next.slug = blogSlug(next.publish_at, next.title);
    }

    setData("blog", next);
  };

  // Under the editor rather than over it: it is a reference to glance back at
  // while writing, not an instruction to read before starting.
  const cheatSheet = (
    <Box
      style={{
        border: "1px solid var(--gray-5)",
        borderRadius: "var(--radius-3)",
        padding: "var(--space-3)",
      }}
    >
      <Text as="p" size="2" weight="medium" mb="2">
        Formatting
      </Text>
      <Grid columns={{ initial: "1", sm: "2" }} gapX="4" gapY="1" mb="3">
        {MARKDOWN_HINTS.map(({ syntax, means }) => (
          <Flex key={syntax} align="center" gap="2" wrap="wrap">
            <Code size="1" variant="soft">
              {syntax}
            </Code>
            <Text size="1" color="gray">
              {means}
            </Text>
          </Flex>
        ))}
      </Grid>
      <Flex direction="column" gap="1">
        {/* The two behaviours nobody would guess from the list above. */}
        <Text size="1" color="gray">
          A YouTube link on a line of its own becomes a player; one in the
          middle of a sentence stays a link.
        </Text>
        <Text size="1" color="gray">
          A single new line is a real line break — three names on three lines
          stay on three lines.
        </Text>
      </Flex>
    </Box>
  );

  const editor = (
    <Flex gap="2" direction="column">
      <Text size="3" weight="medium">
        Body *
      </Text>
      <TextArea
        size="3"
        rows={isMobile ? 12 : 22}
        value={data.blog.body}
        onChange={(e) => setBlog({ body: e.target.value })}
        required
        placeholder={"### Round 3\n\nHow the weekend went…"}
      />
      {cheatSheet}
    </Flex>
  );

  const preview = (
    <Flex gap="2" direction="column">
      <Text size="3" weight="medium">
        Preview
      </Text>
      <Text size="2" color="gray">
        Formatting only — the public page brings its own styling.
      </Text>
      <Box
        style={{
          border: "1px solid var(--gray-5)",
          borderRadius: "var(--radius-3)",
          padding: "var(--space-3)",
          minHeight: "8rem",
          overflowX: "auto",
        }}
      >
        <MarkdownPreview body={data.blog.body} previewPath={paths.preview} />
      </Box>
    </Flex>
  );

  return (
    <Box className="border-2 py-8 px-10">
      <Heading size="7">{title}</Heading>
      <Text size="4" as="p" color="gray">
        Write a post for your club&rsquo;s blog.
      </Text>
      <Separator my="3" size="4" />

      {errors && errors.length > 0 && (
        <Callout.Root color="red" role="alert" mb="4">
          {errors.map((error, index) => (
            <div key={index}>
              <Callout.Icon>
                <AlertCircle />
              </Callout.Icon>
              <Callout.Text>{error}</Callout.Text>
            </div>
          ))}
        </Callout.Root>
      )}

      <form onSubmit={onSubmit}>
        {/* Basics */}
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Title *
              </Text>
              <TextField.Root
                size="3"
                value={data.blog.title}
                maxLength={limits.title}
                onChange={(e) => setBlog({ title: e.target.value })}
                required
                placeholder="e.g. Round 3 Recap"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Link
              </Text>
              <TextField.Root
                size="3"
                value={data.blog.slug}
                onChange={(e) => {
                  setSlugPinned(true);
                  setData("blog", { ...data.blog, slug: e.target.value });
                }}
                placeholder="fills in from the title"
              />
              <Text size="2" color="gray">
                This is probably how it&rsquo;ll appear: /fb/blogs/
                {data.blog.slug || "…"}. Edit it if you&rsquo;d rather; once a
                post is published its link stays put.
              </Text>
            </Flex>
          </Box>

          <Grid columns={{ initial: "1", sm: "2" }} gap="5">
            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">
                  Author *
                </Text>
                <TextField.Root
                  size="3"
                  value={data.blog.author}
                  onChange={(e) => setBlog({ author: e.target.value })}
                  required
                  placeholder="e.g. Team Final Bout"
                >
                  <TextField.Slot>
                    <User height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Box>

            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">
                  Publish date
                </Text>
                <TextField.Root
                  type="datetime-local"
                  size="3"
                  value={data.blog.publish_at}
                  onChange={(e) => setBlog({ publish_at: e.target.value })}
                >
                  <TextField.Slot>
                    <CalendarClock height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
                <Text size="2" color="gray">
                  Leave it blank to keep this a draft. A date in the future
                  schedules it — the post appears on its own.
                </Text>
              </Flex>
            </Box>
          </Grid>
        </Grid>

        {/* Content. Side by side where there is room; a tab switch where there
            isn't, because a preview under a 12-row textarea is off-screen. */}
        <Heading size="5" weight="bold">
          Content
        </Heading>
        <Separator my="3" size="4" />

        {isMobile ? (
          <Tabs.Root defaultValue="write">
            <Tabs.List>
              <Tabs.Trigger value="write">Write</Tabs.Trigger>
              <Tabs.Trigger value="preview">Preview</Tabs.Trigger>
            </Tabs.List>
            <Box pt="3">
              <Tabs.Content value="write">{editor}</Tabs.Content>
              <Tabs.Content value="preview">{preview}</Tabs.Content>
            </Box>
          </Tabs.Root>
        ) : (
          <Grid columns="2" gap="5" mb="6">
            {editor}
            {preview}
          </Grid>
        )}

        <Box mt="6">
          <Flex gap="4" justify="end">
            <Button
              type="button"
              size="3"
              color="gray"
              variant="soft"
              onClick={() => history.back()}
            >
              Cancel
            </Button>
            <Button size="3" type="submit" disabled={processing}>
              {submitLabel}
            </Button>
          </Flex>
        </Box>
      </form>
    </Box>
  );
}
